import { CircularProgress } from '@mui/material';

import { Dialog } from '@/components/common/Dialog';
import { Issue, IssueUrgency } from '@/models/Issue';

export interface RfqGenerationProgress {
  done: number;
  total: number;
}

export interface RfqGenerationDialogProps {
  open: boolean;
  issues: Issue[];
  progress: RfqGenerationProgress | null;
}

const font = "'Manrope', sans-serif";
const C = { text: '#f3f3f3', muted: '#bbb7af', subtle: '#737373', accent: '#c0ff02' };

// Same urgency → color mapping used by the issue rows that feed this exact
// flow (see HomeownerHub.tsx's `plan.map(...)` list above the "Prepare bid
// request" button, and SEV_COLORS there).
const SEV_COLORS: Record<IssueUrgency, string> = {
  [IssueUrgency.Critical]: '#DB5282',
  [IssueUrgency.Urgent]: '#FF9A4D',
  [IssueUrgency.Moderate]: '#FFF981',
  [IssueUrgency.Low]: '#5AE9FF',
  [IssueUrgency.Monitor]: C.muted,
};

const getIssueCategory = (issue: Issue) =>
  issue.trade ?? issue.componentCategory ?? issue.category ?? 'Repair item';

const getStatus = (idx: number, done: number, total: number) => {
  if (idx < done) return 'Ready';
  if (idx === Math.min(done, Math.max(total - 1, 0))) return 'Generating';
  return 'Queued';
};

export const RfqGenerationDialog = ({ open, issues, progress }: RfqGenerationDialogProps) => {
  const total = progress?.total ?? issues.length;
  const done = Math.min(progress?.done ?? 0, total);
  const isCompile = total > 1;
  const activeIndex = Math.min(done, Math.max(total - 1, 0));
  const visibleIssues = issues.slice(0, Math.max(total, issues.length));
  const footerLabel =
    done >= total
      ? 'Opening RFQ review…'
      : `Preparing ${activeIndex + 1} of ${total} bid request${total === 1 ? '' : 's'}…`;

  return (
    <Dialog
      open={open}
      width={480}
      disableClose
      disableBackdropClick
      aria-labelledby="rfq-generation-title"
      aria-describedby="rfq-generation-description"
      containerProps={{ gap: 0, p: 0 }}>
      <div style={{ padding: 25 }}>
        <div
          id="rfq-generation-title"
          style={{ fontSize: 17, fontWeight: 600, color: C.text, fontFamily: font, marginBottom: 6 }}>
          {isCompile ? 'Compiling bid requests' : 'Preparing bid request'}
        </div>
        <div
          id="rfq-generation-description"
          style={{ fontSize: 13, color: C.subtle, fontFamily: font, lineHeight: 1.6, marginBottom: 20 }}>
          {isCompile
            ? "We're turning these repair items into contractor-ready RFQs."
            : "We're turning this repair item into a contractor-ready RFQ."}
        </div>

        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
            maxHeight: 300,
            overflowY: 'auto',
          }}>
          {visibleIssues.map((issue, idx) => {
            const status = getStatus(idx, done, total);
            const sevColor = SEV_COLORS[issue.urgency] ?? C.muted;

            return (
              <div
                key={issue.id}
                data-testid="rfq-generation-issue"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '12px 16px',
                  background: 'var(--tx-fill)',
                  borderRadius: 12,
                  opacity: status === 'Queued' ? 0.6 : 1,
                }}>
                <div
                  style={{ width: 4, height: 32, borderRadius: 2, background: sevColor, flexShrink: 0 }}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 12,
                      fontWeight: 600,
                      color: C.text,
                      fontFamily: font,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}>
                    {issue.title}
                  </div>
                  <div style={{ fontSize: 10, color: C.muted, fontFamily: font, marginTop: 2 }}>
                    {[getIssueCategory(issue), issue.urgency].filter(Boolean).join(' · ')}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            fontSize: 12,
            color: C.subtle,
            fontFamily: font,
            marginTop: 20,
          }}>
          <CircularProgress size={16} thickness={4} sx={{ color: C.accent }} />
          {footerLabel}
        </div>
      </div>
    </Dialog>
  );
};
